import debounce from 'lodash.debounce'
import {
  Room,
  sendReceivedMessage,
  sendSyncSeen,
} from '@weblite/ts-action/room'
import { Message, reqGetGroupSeen } from '@weblite/ts-action/message'
import { GetGroupSeen } from '@weblite/ts-action/message/client'
import * as roomSeenSync from '$logic/room-seen/offline-sync'
import { request, send } from '$setup/ws'
import {
  DELETED_MARKER_WIDTH,
  getRoom,
  getStorageByRoomTypeAndRoomId,
  storageByRoomType,
  updateRoom,
} from '$logic/room'
import { handleSeenMessagesNotification } from '$logic/push/service'
import { setSeenMention } from '$logic/mention'
import { isMessageFromClientUser } from '$logic/message'
import { getRoomLastMessage } from '$logic/room-last-message'
import { getClientUserId } from '$logic/client-user'

const DEBOUNCE_WAIT_FOR_SYNCING_SEEN = 1000

function countBinaryOnes(value: number, startIndex: number, endIndex: number) {
  return value
    .toString(2)
    .split('')
    .reverse()
    .slice(startIndex, endIndex)
    .filter(ch => ch === '1').length
}

export function calculateUnseenCount(
  lastMessage: Message | null,
  clientSeen: number | undefined,
  room: Room | undefined,
) {
  if (!room || clientSeen === undefined) return 0
  const lastMessageIid = lastMessage?.iid
  if (!lastMessageIid) return 0
  const { iid: roomLastIid, deletedMessageMarker } = room

  const deletedUnseen = countBinaryOnes(
    deletedMessageMarker ?? 0,
    roomLastIid - lastMessageIid,
    Math.min(roomLastIid - clientSeen, DELETED_MARKER_WIDTH),
  )

  return Math.max(0, lastMessageIid - clientSeen - deletedUnseen)
}

export async function getRoomUnseenCountAndUnreadMark(
  roomId: string,
): Promise<{ unseenCount: number; unreadMark: boolean }> {
  const room = await getRoom(roomId)
  const data = await storageByRoomType(room.type).get(roomId)
  const lastMessage = await getRoomLastMessage(roomId)
  const unseenCount =
    data && 'seen' in data
      ? calculateUnseenCount(lastMessage, data.seen, room)
      : 0

  return { unseenCount, unreadMark: data?.unreadMark }
}

export async function syncSeen() {
  const items = await roomSeenSync.getAll()
  if (!items.length) {
    return
  }
  send(sendSyncSeen({ patches: items }), true, 1)
}

const syncSeenWithDelay = debounce(syncSeen, DEBOUNCE_WAIT_FOR_SYNCING_SEEN)

export async function setSeen(
  roomId: string,
  seen: number,
  fromMe: boolean,
): Promise<void> {
  const room = await getRoom(roomId)
  if (room && room.type === 'PRIVATE_SPACE') return

  setSeenMention(roomId, seen)

  const { get, set } = getStorageByRoomTypeAndRoomId(roomId, room.type)

  const oldValues = await get(roomId)

  handleSeenMessagesNotification(roomId, seen)

  if (!oldValues || seen <= oldValues.seen) return

  await set(roomId, { seen })

  await roomSeenSync.set(
    roomId,
    seen,
    room!.type,
    fromMe || seen <= room!.seenIid,
  )

  syncSeenWithDelay()
}

/**
 * Use cases:
 * - Whenever there is new received message from server
 * - After room first update, if their iid has been changed
 */
export async function setReceived(
  patches: { roomId: string; receivedIid: number }[],
): Promise<void> {
  const affectedRooms = (
    await Promise.all(
      patches.map(async ({ roomId, receivedIid: iid }) => {
        const { type, receivedIid } = await getRoom(roomId)
        if (type === 'CHANNEL' || type === 'PRIVATE_SPACE') return null

        const lastMessage = await getRoomLastMessage(roomId)
        if (!lastMessage || isMessageFromClientUser(lastMessage)) return null

        const received = Math.min(lastMessage.iid, iid)

        if (received <= receivedIid) return null

        return { type, roomId, receivedIid: received }
      }),
    )
  ).filter(Boolean)

  if (affectedRooms.length) {
    send(sendReceivedMessage({ patches: affectedRooms }), true, 1)
  }
}

export async function setSeenRoomAllMessages(roomId: string) {
  const lastMessage = await getRoomLastMessage(roomId)
  if (lastMessage)
    await setSeen(roomId, lastMessage.iid, isMessageFromClientUser(lastMessage))
}

export async function getGroupSeen(roomId: string, iid: number) {
  const res = await request<GetGroupSeen>(reqGetGroupSeen({ roomId, iid }))
  if (res.error) {
    console.error(res.error)
    return []
  }
  const clientUserId = getClientUserId()
  const viewers = res.payload.viewers.filter(
    ({ userId }) => userId !== clientUserId,
  )

  return viewers.sort((a, b) => +new Date(b.seenAt) - +new Date(a.seenAt))
}

export function removeOfflineSync(roomId: string) {
  return roomSeenSync.del([roomId])
}
